import type { FeatureEvents, ScreenEvent, TrackEvent, UIEvent } from '../new-analytics-types';
import { Action, ActionSubject, TrackAction, TrackActionSubject } from '../new-analytics-types';

type ScreenKeys = 'mcpSettingsScreen' | 'mcpSettingsAddDomainModal' | 'mcpSettingsDeleteDomainModal';
type UIEventKeys = 'mcpSettingsAddDomainSubmit' | 'mcpSettingsDeleteDomainConfirm';
type TrackEventKeys =
  | 'mcpSettingsToggleAtlassianDomainsSuccess'
  | 'mcpSettingsToggleAtlassianDomainsFailure'
  | 'mcpSettingsAddDomainSuccess'
  | 'mcpSettingsAddDomainFailure'
  | 'mcpSettingsDeleteDomainSuccess'
  | 'mcpSettingsDeleteDomainFailure';

export enum McpSettingsScreens {
  mcpSettingsScreen = 'mcpSettingsScreen',
  mcpSettingsAddDomainModal = 'mcpSettingsAddDomainModal',
  mcpSettingsDeleteDomainModal = 'mcpSettingsDeleteDomainModal',
}

const screen = {
  // https://data-portal.internal.atlassian.com/analytics/registry/91148
  mcpSettingsScreen: (): ScreenEvent => ({
    data: {
      name: McpSettingsScreens.mcpSettingsScreen,
    },
    dataPortalLink: 'https://data-portal.internal.atlassian.com/analytics/registry/91148',
  }),
  // https://data-portal.internal.atlassian.com/analytics/registry/91291
  mcpSettingsAddDomainModal: (): ScreenEvent => ({
    data: {
      name: McpSettingsScreens.mcpSettingsAddDomainModal,
    },
    dataPortalLink: 'https://data-portal.internal.atlassian.com/analytics/registry/91291',
  }),
  // https://data-portal.internal.atlassian.com/analytics/registry/91292
  mcpSettingsDeleteDomainModal: (): ScreenEvent => ({
    data: {
      name: McpSettingsScreens.mcpSettingsDeleteDomainModal,
    },
    dataPortalLink: 'https://data-portal.internal.atlassian.com/analytics/registry/91292',
  }),
};

const ui = {
  // https://data-portal.internal.atlassian.com/analytics/registry/91294
  mcpSettingsAddDomainSubmit: (): UIEvent => ({
    data: {
      action: Action.Clicked,
      actionSubject: ActionSubject.Button,
      actionSubjectId: 'mcpSettingsAddDomainSubmit',
      source: McpSettingsScreens.mcpSettingsAddDomainModal,
    },
    dataPortalLink: 'https://data-portal.internal.atlassian.com/analytics/registry/91294',
  }),
  // https://data-portal.internal.atlassian.com/analytics/registry/91295
  mcpSettingsDeleteDomainConfirm: (): UIEvent => ({
    data: {
      action: Action.Clicked,
      actionSubject: ActionSubject.Button,
      actionSubjectId: 'mcpSettingsDeleteDomainConfirm',
      source: McpSettingsScreens.mcpSettingsDeleteDomainModal,
    },
    dataPortalLink: 'https://data-portal.internal.atlassian.com/analytics/registry/91295',
  }),
};

const track = {
  // Toggle Operations
  // https://data-portal.internal.atlassian.com/analytics/registry/91300
  mcpSettingsToggleAtlassianDomainsSuccess: (attributes: { enabled: boolean }): TrackEvent => ({
    data: {
      action: TrackAction.Toggled,
      actionSubject: TrackActionSubject.AtlassianSupportedMCPDomains,
      actionSubjectId: 'mcpSettingsToggleAtlassianDomainsSuccess',
      source: McpSettingsScreens.mcpSettingsScreen,
      attributes,
    },
    dataPortalLink: 'https://data-portal.internal.atlassian.com/analytics/registry/91300',
  }),
  // https://data-portal.internal.atlassian.com/analytics/registry/91312
  mcpSettingsToggleAtlassianDomainsFailure: (): TrackEvent => ({
    data: {
      action: TrackAction.ChangeFailed,
      actionSubject: TrackActionSubject.AtlassianSupportedMCPDomains,
      actionSubjectId: 'mcpSettingsToggleAtlassianDomainsFailure',
      source: McpSettingsScreens.mcpSettingsScreen,
    },
    dataPortalLink: 'https://data-portal.internal.atlassian.com/analytics/registry/91312',
  }),

  // Add Domain Operations
  // https://data-portal.internal.atlassian.com/analytics/registry/91296
  mcpSettingsAddDomainSuccess: (attributes?: { domainPattern?: string }): TrackEvent => ({
    data: {
      action: TrackAction.Added,
      actionSubject: TrackActionSubject.MCPDomain,
      actionSubjectId: 'mcpSettingsAddDomainSuccess',
      source: McpSettingsScreens.mcpSettingsAddDomainModal,
      attributes,
    },
    dataPortalLink: 'https://data-portal.internal.atlassian.com/analytics/registry/91296',
  }),

  // https://data-portal.internal.atlassian.com/analytics/registry/91299
  mcpSettingsAddDomainFailure: (): TrackEvent => ({
    data: {
      action: TrackAction.CreationFailed,
      actionSubject: TrackActionSubject.MCPDomain,
      actionSubjectId: 'mcpSettingsAddDomainFailure',
      source: McpSettingsScreens.mcpSettingsAddDomainModal,
    },
    dataPortalLink: 'https://data-portal.internal.atlassian.com/analytics/registry/91299',
  }),

  // Delete Domain Operations
  // https://data-portal.internal.atlassian.com/analytics/registry/91298
  mcpSettingsDeleteDomainSuccess: (): TrackEvent => ({
    data: {
      action: TrackAction.Deleted,
      actionSubject: TrackActionSubject.MCPDomain,
      actionSubjectId: 'mcpSettingsDeleteDomainSuccess',
      source: McpSettingsScreens.mcpSettingsDeleteDomainModal,
    },
    dataPortalLink: 'https://data-portal.internal.atlassian.com/analytics/registry/91298',
  }),
  // https://data-portal.internal.atlassian.com/analytics/registry/91313
  mcpSettingsDeleteDomainFailure: (): TrackEvent => ({
    data: {
      action: TrackAction.ChangeFailed,
      actionSubject: TrackActionSubject.MCPDomain,
      actionSubjectId: 'mcpSettingsDeleteDomainFailure',
      source: McpSettingsScreens.mcpSettingsDeleteDomainModal,
    },
    dataPortalLink: 'https://data-portal.internal.atlassian.com/analytics/registry/91313',
  }),
};

export const mcpSettingsEvents: FeatureEvents<ScreenKeys, UIEventKeys, TrackEventKeys, string> = {
  confluenceSpec: 'https://hello.atlassian.net/wiki/spaces/mcp/pages/5978033203/MCP+Settings+AdminHub+-+Analytics+Spec',
  screen,
  ui,
  track,
  operational: {},
};
